import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { useMatch, useParams } from 'react-router-dom';

// Material-UI imports
import Typography from "@mui/material/Typography";

// Local component imports
import CoursePayment from "../CoursePayment";
import InformationPoint from "./InformationPoint";
import LocationQuestion from "./LocationQuestion";
import TeamNameInGamePrompt from "../gameSetupAndLobby/TeamNameInGamePrompt";
import QuestionParent from "./QuestionParent";
import RateUs from "../../pages/RateUs";
import Tipping from "../../pages/Tipping";

// Utility/helper imports
import { courses } from "../courseContent/courseObjects";
import { clueTypes } from "../courseContent/questionTypes";
import * as TYPES from "../courseContent/questionTypes";
import { ToEnd, ToNextQuestion, ToQuestionAnswer, ToQuestion } from "../helpers/routes";
import { getFirstClueQuestionKey } from "../../helpers/helpers";
import { markTourAutoShownForCourse, updateTour } from "../../redux/uiSlice";

const CourseContentContainer = (props) => {
  //use URL to figure out which course and question we are on
  const {course, question} = useParams();
  const currentCourseName = course || null;
  const currentQuestionNum = Number(question) || null;
  const onAnswer = Boolean(useMatch('/course/:course/q/:question/answer'));

  const dispatch = useDispatch();
  const tourAutoShownForCourse = useSelector((state) => state.ui?.tourAutoShownForCourse);
  const questions = useSelector((state) => state.team?.teamDetails?.questions) || {};

  const currentCourseContent = currentCourseName ? courses[currentCourseName]?.courseContent : null;
  const firstClueQuestionKey = useMemo(
    () => getFirstClueQuestionKey(currentCourseContent),
    [currentCourseContent]
  );
  const currentCourseKeys = currentCourseContent ? Object.keys(currentCourseContent) : [];
  const currentQuestionKey = currentCourseKeys[currentQuestionNum || 0];
  const currentQuestionContent = currentCourseContent?.[currentQuestionKey];
  const questionData = questions[currentQuestionKey];
  const answeredIncorrect = onAnswer && questionData?.userActions
      ? !questionData?.userActions?.includes("success")
      : false;

  useEffect(() => {
    const isFirstClueAnswer =
      onAnswer &&
      clueTypes.includes(currentQuestionContent?.type) &&
      currentQuestionKey === firstClueQuestionKey;

    if (
      isFirstClueAnswer &&
      currentCourseName &&
      tourAutoShownForCourse !== currentCourseName
    ) {
      dispatch(updateTour(true));
      dispatch(markTourAutoShownForCourse(currentCourseName));
    }
  }, [
    onAnswer,
    currentQuestionContent?.type,
    currentQuestionKey,
    firstClueQuestionKey,
    currentCourseName,
    tourAutoShownForCourse,
    dispatch,
  ]);

  if (!currentCourseName || !courses[currentCourseName]) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <Typography variant="h6" color="error">
          Course not found: {currentCourseName}
        </Typography>
        <Typography>
          Please select a valid course from the course selector.
        </Typography>
      </div>
    );
  }
  
  //get the content for the current question
  //determine what the url for the next item is
  let nextURL;
  if (!onAnswer && currentQuestionContent?.answer) { //if not on an answer and an answer exists, than next url is answer
    nextURL = ToQuestionAnswer(currentCourseName, currentQuestionNum || 0);
  } else if (currentCourseKeys.length - 1 === currentQuestionNum) { //if its the last object, the nex URL is end
    nextURL = ToEnd(currentCourseName);
  } else {
    nextURL = ToNextQuestion(currentCourseName, currentQuestionNum || 0);
  }

  //calculate n of n questions
  let arrayOfQuestionsForLocation = [];
  let nthQuestionForLocation = 0;
  for (const [key, value] of Object.entries(currentCourseContent)) {
    if (clueTypes.includes(value.type) && currentQuestionContent?.location === value?.location) {
      arrayOfQuestionsForLocation.push(key);
      if( key === currentQuestionKey ){
        nthQuestionForLocation = arrayOfQuestionsForLocation.length;
      }
    }
  }
  const totalQuestionsForLocation = arrayOfQuestionsForLocation.length;
  const nOfNQuestions = <Typography variant={'caption'}
                                         color={'primary'}> {nthQuestionForLocation + " of " + totalQuestionsForLocation} </Typography>;

  //determine which container to use
  let containerType;
  switch (currentQuestionContent?.type) {
    case TYPES.freeTextQuestion: //nothing makes it like an or statement
    case TYPES.multipleChoice:
    case TYPES.mixAndMatch:
      containerType = (<QuestionParent {...currentQuestionContent} nextURL={nextURL} questionKey={currentQuestionKey} pageURL={ToQuestion(currentCourseName, currentQuestionNum)} nOfNQuestions={nOfNQuestions} />);
      break;

    case TYPES.newLocation:
      containerType = (<LocationQuestion {...props} {...currentQuestionContent} nextURL={nextURL} />);
      break;
    case TYPES.locationDone:
      containerType = (<InformationPoint  locationDone {...currentQuestionContent} nextURL={nextURL} />);
      break;
    case TYPES.tip:
      containerType = (<Tipping  nextURL={nextURL} />);
      break;
    case TYPES.teamName:
      containerType = (<TeamNameInGamePrompt nextURL={nextURL} />);
      break;
    case TYPES.rateUs:
      containerType = (<RateUs  nextURL={nextURL} />);
      break;
    case TYPES.payUs:
      containerType = (<CoursePayment {...props}  nextURL={nextURL} />);
      break;
    case TYPES.informational:
    default:
      containerType = (<InformationPoint type={TYPES.informational} {...currentQuestionContent} nextURL={nextURL} />);
      break;
  }
  if (onAnswer) {
    containerType = (<InformationPoint  type={TYPES.answer} {...currentQuestionContent.answer} location={currentQuestionContent.location}
                                        nextURL={nextURL} answeredIncorrect={answeredIncorrect}
                                        nOfNQuestions={currentQuestionContent?.type === TYPES.freeTextQuestion && nOfNQuestions} />);
  }

  return (<>
    {containerType}
    </>)
};

export default CourseContentContainer;